import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleOutlined } from "@ant-design/icons";
import { Modal } from 'antd'
import ReCAPTCHA from 'react-google-recaptcha';

const ThankYouModal = ({ onClose, onNavigate }) => {
  return (
    <Modal
      open={true}
      centered
      // width={448}
      classNames={{ body: 'text-center' }}
      onCancel={onClose}
      onOk={onNavigate}
      // okText="Got it, thanks!"
      footer={
        <button
          onClick={onNavigate}
          className="btn-solid w-full text-[1rem]"
        >
          Got it, thanks!
        </button>
      }
      cancelButtonProps={{ style: { display: "none" } }}
      styles={{
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
        content: {
          padding: "2rem",
          borderRadius: "1rem",
          textAlign: "center",
        },
        footer: {
          marginTop: "2rem",
        },
      }}
    >
      {/* Success Icon */}
      <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full">
        <CheckCircleOutlined className="text-5xl text-green-600" />
      </div>

      {/* Text */}
      <h3 className="mb-3 text-2xl font-extrabold text-text-heading md:text-3xl">
        Application Received!
      </h3>

      <p className="mb-0 text-base leading-relaxed text-text-muted">
        Thank you for applying to our wholesale program.
        <br />
        Our team will review your details and get back to you within{" "}
        <strong>24–48 hours</strong>.
      </p>
    </Modal>
  );
};

const Home = () => {
  // const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    storeName: '',
    website: '',
    taxId: '',
    email: '',
    description: ''
  });

  const [errors, setErrors] = useState({
    website: '',
    email: ''
    // description validation disabled
  });

  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Triggered when the user completes the checkbox challenge
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  // Optional: Flexible website URL validation
  const validateWebsite = (value) => {
    if (!value) return '';
    const regex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/.*)?$/i;
    return regex.test(value)
      ? ''
      : 'Please enter a valid website URL';
  };

  // Standard Email Validation (Any domain allowed)
  const validateEmail = (value) => {
    if (!value) return '';
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value) ? '' : 'Please enter a valid email address';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Email live updates and validation
    if (name === 'email') {
      const lowerValue = value.toLowerCase();
      setFormData(prev => ({ ...prev, email: lowerValue }));
      setErrors(prev => ({ ...prev, email: validateEmail(lowerValue) }));
      return;
    }

    // Website live updates and validation
    if (name === 'website') {
      setFormData(prev => ({ ...prev, website: value }));
      setErrors(prev => ({ ...prev, website: validateWebsite(value) }));
      return;
    }

    // Standard field handler (including optional description)
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setErrors({ captchaError: "Please complete the CAPTCHA validation first!" });
      return;
    }

    const websiteError = validateWebsite(formData.website);
    const emailError = validateEmail(formData.email);

    if (websiteError || emailError) {
      setErrors({ website: websiteError, email: emailError });
      return;
    }

    setIsSubmitting(true);

    const scriptURL = process.env.REACT_APP_GOOGLE_SHEET_FORM_URL;

    if (!scriptURL) {
      console.error('Google Sheet URL is missing in .env file!');
      setIsSubmitting(false);
      return;
    }

    const params = new URLSearchParams();
    params.append('storeName', formData.storeName);
    params.append('website', formData.website);
    params.append('taxId', formData.taxId);
    params.append('email', formData.email);
    params.append('description', formData.description || '');
    params.append('captchaToken', captchaToken);

    try {
      fetch(scriptURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': '*/*'
        },
        body: params,
        redirect: 'follow',
      }).then(response => response.json())
      .then((result) => {
        if(!result.success) {
          throw new Error('Form submission failed: ' + result.message);
        }
        setFormData({
          storeName: '',
          website: '',
          taxId: '',
          email: '',
          description: '',
        });
        setErrors({ website: '', email: '' });
        setSubmitted(true);
      })
      .catch(error => {
        throw new Error('Error submitting form: ' + error.message)
      });

      // Show thank you popup
      setSubmitted(true);

    } catch (error) {
      console.error('Error!', error.message);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setSubmitted(false);
  };

  const handleModalNavigate = () => {
    setSubmitted(false);
  };

  const categories = [
    {
      title: "Video Games",
      icon: "🎮",
      desc: "Wholesale sourcing for current, retro, regional, and imported video game software."
    },
    {
      title: "Trading Card Games",
      icon: "🃏",
      desc: "Imported TCG offers for TCG retailers."
    },
    {
      title: "Consoles & Accessories",
      icon: "🕹️",
      desc: "Hardware, peripherals, and gaming accessories sourced globally"
    },
    {
      title: "Import & Global Sourcing",
      icon: "🌍",
      desc: "Cross-border sourcing support for products from Japan, Europe, and broader international markets."
    }
  ];

  const stats = [
    { label: "Established", value: "2006" },
    { label: "Sourcing Network", value: "Global" },
    { label: "Distribution Focus", value: "U.S." },
    { label: "Retail Partnerships", value: "B2B" }
  ];

  return (
    <div className="pb-20" id="home">

      {/* Thank You Popup Modal */}
      {submitted && (
        <ThankYouModal
          onClose={handleModalClose}
          onNavigate={handleModalNavigate}
        />
      )}

      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-bg-light to-white" id="about">
        <div className="container mx-auto px-6 md:px-8 grid lg:grid-cols-[1.2fr_0.8fr] gap-10 md:gap-16 items-center mb-12 md:mb-20">
          <div>
            <span className="text-primary-red font-bold text-[0.7rem] md:text-[0.8rem] tracking-wider border border-primary-light-border px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-primary-light inline-block">ESTABLISHED SINCE 2006</span>
            <p className="mt-4 md:mt-6 text-[0.7rem] md:text-[0.75rem] font-bold text-primary-red tracking-[1.5px] uppercase">GLOBAL SOURCING • WHOLESALE DISTRIBUTION</p>
            <h1 className="text-[2.2rem] md:text-[3.5rem] font-extrabold leading-[1.15] md:leading-[1.1] my-4 md:my-6 text-text-heading">
              Global Distribution of Video Games, TCG & Entertainment Products
            </h1>
            <p className="text-[1rem] md:text-[1.1rem] text-text-muted mb-8 md:mb-10 max-w-[600px]">
              Trusted wholesale partner for imported video games,
              trading card games, consoles, accessories, and globally sourced entertainment products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/#apply" className="btn-solid w-full sm:w-auto text-center inline-block">Apply for Wholesale Account</Link>
            </div>
          </div>

          <div className="card-shadow p-6 md:p-10" id="why-choose">
            <h3 className="mb-4 md:mb-6 text-[1.1rem] md:text-[1.2rem] font-bold">Why Choose BASCO</h3>
            <ul className="space-y-4 md:space-y-[1.2rem]">
              <li className="flex gap-4">
                <span className="w-2.5 h-2.5 bg-primary-red rounded-full flex-shrink-0 mt-1.5"></span>
                <p className="text-[0.95rem] text-text-muted"><strong>Global</strong> supplier network across Japan, Europe and Asia</p>
              </li>
              <li className="flex gap-4">
                <span className="w-2.5 h-2.5 bg-primary-red rounded-full flex-shrink-0 mt-1.5"></span>
                <p className="text-[0.95rem] text-text-muted"><strong>Fast U.S. fulfillment</strong> and competitive wholesale pricing</p>
              </li>
              <li className="flex gap-4">
                <span className="w-2.5 h-2.5 bg-primary-red rounded-full flex-shrink-0 mt-1.5"></span>
                <p className="text-[0.95rem] text-text-muted"><strong>Exclusive</strong> import access for high-demand titles and products</p>
              </li>
              <li className="flex gap-4">
                <span className="w-2.5 h-2.5 bg-primary-red rounded-full flex-shrink-0 mt-1.5"></span>
                <p className="text-[0.95rem] text-text-muted"><strong>Dedicated account support</strong> built for long-term partners</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Stats Row */}
        <div className="container mx-auto px-6 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" id="stats">
          {stats.map((stat, idx) => (
            <div key={idx} className="card-shadow p-4 md:p-8 text-center">
              <h2 className="text-[1.5rem] md:text-[2rem] mb-1 md:mb-2 font-bold text-primary-red">{stat.value}</h2>
              <p className="text-[0.75rem] md:text-[0.9rem] text-text-muted font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories Section */}
      <section className="container mx-auto px-6 md:px-8 py-12 md:py-20" id="categories">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-[1.8rem] md:text-[2.5rem] mb-3 md:mb-4 font-bold">Distribution Categories</h2>
          <p className="text-text-muted text-[0.9rem] md:text-base max-w-[700px] mx-auto">Built for customers looking for reliable supply, import access, and long-term wholesale support.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {categories.map((cat, idx) => (
            <div key={idx} className="card-shadow p-6 md:p-10 text-center transition-all duration-300 hover:-translate-y-1">
              <div className="text-[1.5rem] md:text-[2rem] mb-4 md:mb-6 bg-primary-light w-[50px] md:w-[60px] h-[50px] md:h-[60px] flex items-center justify-center rounded-xl mx-auto">{cat.icon}</div>
              <h3 className="text-[1rem] md:text-[1.1rem] mb-2 md:mb-4 font-bold">{cat.title}</h3>
              <p className="text-[0.8rem] md:text-[0.85rem] text-text-muted">{cat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Wholesale Partner Program */}
      <section className="container mx-auto px-6 md:px-8 py-12 md:py-20" id="wholesale">
        <div className="flex flex-col lg:flex-row gap-12 md:gap-20 items-start justify-between">
          <div className="lg:flex-1 lg:self-start">
            <h2 className="text-[1.8rem] md:text-[2.5rem] mt-0 mb-4 md:mb-6 font-bold leading-none">Wholesale Partner Program</h2>
            <p className="text-text-muted mb-8 md:mb-10 text-[1rem] md:text-[1.1rem]">Access wholesale pricing, preorder opportunities and inventory updates.</p>
            <ul className="space-y-4 md:space-y-[1.2rem]">
              <li className="flex items-center gap-3 md:gap-4 font-semibold text-[0.85rem] md:text-[0.95rem] p-3 md:p-4 bg-white rounded-xl shadow-sm border border-border-light">
                <span className="text-white bg-primary-red w-5 md:w-6 h-5 md:h-6 flex-shrink-0 flex items-center justify-center rounded-full text-[0.7rem] md:text-[0.75rem]">✓</span> Dealer pricing access
              </li>
              <li className="flex items-center gap-3 md:gap-4 font-semibold text-[0.85rem] md:text-[0.95rem] p-3 md:p-4 bg-white rounded-xl shadow-sm border border-border-light">
                <span className="text-white bg-primary-red w-5 md:w-6 h-5 md:h-6 flex-shrink-0 flex items-center justify-center rounded-full text-[0.7rem] md:text-[0.75rem]">✓</span> MOQ visibility
              </li>
              <li className="flex items-center gap-3 md:gap-4 font-semibold text-[0.85rem] md:text-[0.95rem] p-3 md:p-4 bg-white rounded-xl shadow-sm border border-border-light">
                <span className="text-white bg-primary-red w-5 md:w-6 h-5 md:h-6 flex-shrink-0 flex items-center justify-center rounded-full text-[0.7rem] md:text-[0.75rem]">✓</span> Import release schedules
              </li>
              <li className="flex items-center gap-3 md:gap-4 font-semibold text-[0.85rem] md:text-[0.95rem] p-3 md:p-4 bg-white rounded-xl shadow-sm border border-border-light">
                <span className="text-white bg-primary-red w-5 md:w-6 h-5 md:h-6 flex-shrink-0 flex items-center justify-center rounded-full text-[0.7rem] md:text-[0.75rem]">✓</span> Dedicated account support
              </li>
            </ul>
          </div>

          <div className="lg:flex-1 w-full" id="apply">
            <h3 className="text-[1.3rem] md:text-[1.5rem] mt-0 mb-6 md:mb-8 font-bold leading-none">Apply Today</h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-[1.2rem]">
              <input
                type="text"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                required
                placeholder="Store Name"
                className="p-3 md:p-4 border border-border-light rounded-lg text-[0.9rem] md:text-[0.95rem] focus:ring-2 focus:ring-primary-red/20 outline-none transition-all"
              />

              <div>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  required
                  placeholder="Website"
                  className={`w-full p-3 md:p-4 border rounded-lg text-[0.9rem] md:text-[0.95rem] focus:ring-2 outline-none transition-all ${
                    errors.website ? 'border-red-500 focus:ring-red-200' : 'border-border-light focus:ring-primary-red/20'
                  }`}
                />
                {errors.website && (
                  <p className="text-red-500 text-xs mt-1">{errors.website}</p>
                )}
              </div>

              <input
                type="text"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                required
                placeholder="Reseller Certificate / Tax ID"
                className="p-3 md:p-4 border border-border-light rounded-lg text-[0.9rem] md:text-[0.95rem] focus:ring-2 focus:ring-primary-red/20 outline-none transition-all"
              />

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Business Email"
                  className={`w-full p-3 md:p-4 border rounded-lg text-[0.9rem] md:text-[0.95rem] focus:ring-2 outline-none transition-all ${
                    errors.email ? 'border-red-500 focus:ring-red-200' : 'border-border-light focus:ring-primary-red/20'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your business, channels, or purchasing volume (Optional)"
                  className="w-full p-3 md:p-4 border border-border-light rounded-lg text-[0.9rem] md:text-[0.95rem] h-[100px] md:h-[120px] resize-none focus:ring-2 focus:ring-primary-red/20 outline-none transition-all"
                ></textarea>
              </div>

              <div>
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_CAPTCHA_KEY}
                  onChange={handleCaptchaChange}
                />
                {errors.captchaError && (
                  <p className="text-red-500 text-xs mt-1">{errors.captchaError}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn-solid w-full text-[1rem] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;